/**
 * SSE Number Series Manager
 *
 * Called by sseEventHandler for an 'nSerie' action (event 'nserie.advanced'):
 * a booking took a number from a series, and the series moved on.
 *
 * Two things follow, in this order:
 *   1. The module's cached settings (the query keyed by its settings endpoint,
 *      e.g. ['salesSettings']) take the new next and last number. Patched in
 *      place rather than invalidated: pages read settings with staleTime
 *      Infinity, and an invalidated query hands a page that mounts meanwhile
 *      the old cached value while it refetches — the stale number again.
 *   2. NSERIE_ADVANCED is dispatched on window, for a new document that is
 *      already open (CardViewForm) to swap in the next number if it still shows
 *      the one it was offered.
 */

export const NSERIE_ADVANCED = 'uapp:nserie.advanced';

/**
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {{ entity: string, settings?: string, id: string, lastValue: string, nextValue: string }} payload
 */
const advance = (queryClient, payload) => {
    if (!payload?.entity || !payload.nextValue) return;

    if (queryClient && payload.settings) {
        queryClient.setQueryData([payload.settings], (cached) => {
            const settings = cached?.data;
            if (!settings?.nSeries?.[payload.entity]) return cached;
            const moved = { nextValue: payload.nextValue, lastValue: payload.lastValue ?? null };
            return {
                ...cached,
                data: {
                    ...settings,
                    nSeries: { ...settings.nSeries, [payload.entity]: { ...settings.nSeries[payload.entity], ...moved } },
                    ...(Array.isArray(settings.numberSeries) && {
                        numberSeries: settings.numberSeries.map(row => row.id === payload.entity ? { ...row, ...moved, loadedNextValue: payload.nextValue } : row)
                    })
                }
            };
        });
    }

    window.dispatchEvent(new CustomEvent(NSERIE_ADVANCED, { detail: payload }));
};

export const nSerieManager = { advance };
